/* Phiếu kết quả cuộc gọi Telesales: kịch bản, mã kết quả (bắt buộc), lịch hẹn / hẹn gọi lại, mã S sau cuộc gọi */
import React, { useState } from 'react';
import { Alert, Button, DatePicker, Input, Select } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { ChevronDown } from 'lucide-react';
import type { CustomerTagItem } from '../../../api/CustomerTag';
import { CODES, LYDO, SCRIPT } from '../components/Data';
import BookingFields, { Field, bookingError, emptyBooking, type Booking } from './BookingFields';
import { codeLabel, findTag, isDepositTag, isLeadTag, shortTag, suggestStage, type Mang } from './logic';

export interface CallResultValue {
  code: string;
  note: string;
  booking?: Booking;
  callback?: number;
  tagId?: number;
  /** Nhóm lý do làm lúc này — ghi vào phiếu nhu cầu */
  lyDo?: string;
}

/** Mã cần hẹn ngày gọi lại (bắt buộc với KN-02, tuỳ chọn với KN-03 / KN-04) */
const CALLBACK_CODES = ['KN-02', 'KN-03', 'KN-04'];

export default function CallResult({ xung, source, mang, tags, curTag, saving, canNext, inbound, lyDo: initialLyDo, onSave }: {
  xung: string;
  /** Lý do làm lúc này đã có ở phiếu nhu cầu */
  lyDo?: string;
  /** Khách tự gọi đến → đổi câu mở đầu kịch bản */
  inbound?: boolean;
  source: string;
  mang?: Mang;
  tags: CustomerTagItem[];
  curTag?: { id: number; name: string } | null;
  saving?: boolean;
  canNext: boolean;
  onSave: (v: CallResultValue, next: boolean) => void;
}) {
  const [code, setCode] = useState<string>();
  const [note, setNote] = useState('');
  const [booking, setBooking] = useState<Booking>(() => emptyBooking(mang));
  const [cb, setCb] = useState<Dayjs | null>(null);
  const [tagId, setTagId] = useState<number | undefined>(curTag?.id);
  const [lyDo, setLyDo] = useState<string | undefined>(initialLyDo);
  const [err, setErr] = useState<string | null>(null);
  // Kịch bản thu gọn mặc định, bấm tiêu đề để xem
  const [showScript, setShowScript] = useState(false);

  const pick = (c: string) => {
    setCode(c);
    setErr(null);
    const s = suggestStage(c, shortTag(curTag?.name));
    setTagId(s ? findTag(tags, s)?.id ?? curTag?.id : curTag?.id);
    if (c === 'KN-02' && !cb) setCb(dayjs().add(2, 'hour').minute(0).second(0));
  };

  const submit = (next: boolean) => {
    let e: string | null = null;
    if (!code) e = 'Chọn mã kết quả trước khi lưu — không lưu được cuộc gọi không có kết quả.';
    else if (code === 'KN-01') e = bookingError(booking);
    else if (code === 'KN-02' && !cb) e = 'Hẹn gọi lại phải có ngày giờ.';
    else if (code === 'KN-02' && cb && cb.isBefore(dayjs())) e = 'Giờ hẹn gọi lại đã qua.';
    if (e || !code) { setErr(e); return; }
    onSave({
      code,
      note: note.trim(),
      booking: code === 'KN-01' ? booking : undefined,
      callback: CALLBACK_CODES.includes(code) && cb ? cb.valueOf() : undefined,
      tagId,
      lyDo: code.startsWith('KK') ? initialLyDo : lyDo,
    }, next);
  };

  const sc = SCRIPT[(mang || '').toLowerCase()];
  // Mã hiện tại có thể nằm ngoài giai đoạn tiềm năng (khách cũ quay lại) → vẫn đưa vào để hiện đúng tên
  const stageOptions = tags.filter((t) => (isLeadTag(t.name) && !isDepositTag(t.name)) || t.id === curTag?.id).map((t) => ({
    value: t.id,
    label: <span className="inline-flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: t.color || '#94a3b8' }} />{t.name}</span>,
  }));

  return (
    <div className="space-y-4">
      {/* Kịch bản */}
      <div className="rounded-lg border border-[#3e91ab]/25 bg-[#e8f4f7] text-[12.5px] dark:border-[#3e91ab]/40 dark:bg-[#3e91ab]/10">
        <button type="button" onClick={() => setShowScript((v) => !v)} className="flex w-full items-center justify-between px-3 py-2 font-semibold text-[#2c6b7f] dark:text-[#8cc8da]">
          {inbound ? 'Kịch bản nghe khách gọi đến' : 'Kịch bản gọi khách mới'}
          <ChevronDown size={16} className={`transition ${showScript ? 'rotate-180' : ''}`} />
        </button>
        {showScript && (
          <ol className="list-decimal space-y-1.5 px-3 pb-3 pl-7 leading-relaxed text-gray-700 dark:text-gray-300">
            {inbound
              ? <li>Chào, xác nhận tên: “Dạ Phòng khám Khơ Thị xin nghe, em chào {xung} ạ.” Hỏi {xung} gọi về việc gì trước khi tư vấn.</li>
              : <li>Chào, xác nhận tên: “Dạ em chào {xung}, em gọi từ Phòng khám Khơ Thị{source ? `, thấy ${xung} vừa nhắn bên ${source}` : ''} ạ.”</li>}
            <li>Hỏi nhu cầu: vấn đề đang gặp, đã điều trị ở đâu chưa, lý do muốn làm lúc này, rào cản (chi phí, sợ đau, thời gian).</li>
            {sc ? <li>{sc.goi} {sc.say}</li> : <li>Chọn mảng ở tab Nhu cầu để hiện cách gọi tên gói và câu mời khám đúng mảng.</li>}
            <li>Mời khám, chốt ngày giờ + chi nhánh. Khách chưa sẵn sàng → hẹn giờ gọi lại cụ thể.</li>
            <li className="font-medium text-red-600 dark:text-red-400">Không tư vấn y tế, không hứa kết quả, không giảm giá theo %, không báo giá PTTM qua điện thoại.</li>
          </ol>
        )}
      </div>

      {/* Mã kết quả */}
      <div>
        <div className="mb-2 text-[13px] font-semibold text-gray-800 dark:text-gray-100">Kết quả cuộc gọi <span className="text-red-500">*</span></div>
        {CODES.map((g) => (
          <div key={g.grp} className="mb-2.5">
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">{g.grp}</div>
            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {g.items.map((it) => {
                const on = code === it.code;
                return (
                  <button
                    key={it.code}
                    type="button"
                    onClick={() => pick(it.code)}
                    className={`flex items-start gap-2 rounded-lg border px-2.5 py-1.5 text-left text-[12.5px] transition ${on
                      ? 'border-[#3e91ab] bg-[#e8f4f7] text-[#2c6b7f] ring-1 ring-[#3e91ab] dark:bg-[#3e91ab]/15 dark:text-[#8cc8da]'
                      : 'border-gray-200 text-gray-700 hover:border-[#3e91ab]/60 dark:border-gray-700 dark:text-gray-300'}`}
                  >
                    <span className={`shrink-0 font-bold tabular-nums ${on ? '' : g.grp === 'Kết nối' ? 'text-emerald-600' : 'text-gray-400'}`}>{it.code}</span>
                    <span className="min-w-0">{codeLabel(it.code)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {code === 'KN-01' && (
        <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
          <div className="mb-2 text-[13px] font-semibold text-gray-800 dark:text-gray-100">Lịch hẹn</div>
          <BookingFields value={booking} onChange={setBooking} />
        </div>
      )}
      {code && CALLBACK_CODES.includes(code) && (
        <Field label={code === 'KN-02' ? 'Hẹn gọi lại lúc' : 'Hẹn gọi lại lúc (nếu khách hẹn)'} required={code === 'KN-02'}>
          <DatePicker showTime={{ format: 'HH:mm', minuteStep: 15 }} format="DD/MM/YYYY HH:mm" value={cb} onChange={setCb} disabledDate={(d) => d.isBefore(dayjs(), 'day')} className="w-full" />
        </Field>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {/* Không liên lạc được thì không hỏi được lý do */}
        {!code?.startsWith('KK') && (
          <Field label="Nhóm lý do làm lúc này">
            <Select value={lyDo} onChange={setLyDo} allowClear placeholder="— chọn —" popupMatchSelectWidth={false} options={LYDO.map((o) => ({ value: o, label: o }))} />
          </Field>
        )}
        <Field label="Mã S sau cuộc gọi">
          <Select value={tagId} onChange={setTagId} options={stageOptions} placeholder="Giữ nguyên" />
          {code && tagId !== curTag?.id && (
            <div className="mt-1 text-[12px] text-gray-500">Gợi ý theo mã kết quả: {shortTag(curTag?.name) || 'chưa có mã'} → <b>{shortTag(tags.find((t) => t.id === tagId)?.name)}</b></div>
          )}
        </Field>
      </div>
      <Field label="Ghi chú">
        <Input.TextArea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ngắn gọn, không ghi chi tiết bệnh lý" maxLength={200} rows={2} />
      </Field>

      {err && <Alert type="error" showIcon message={err} />}

      <div className="flex flex-wrap gap-2">
        <Button type="primary" loading={saving} onClick={() => submit(false)} className="!bg-[#3e91ab] font-semibold">Lưu kết quả</Button>
        {canNext && <Button loading={saving} onClick={() => submit(true)}>Lưu và gọi khách tiếp</Button>}
      </div>
    </div>
  );
}
